import { cn, useIsMobile } from '@wellesley/ui'
import useActionGuard from 'hooks/useActionGuard'
import { useSwitchState } from 'hooks/useSwitchState'
import useUploadFromClipboard from '@wellesley/uploads/hooks/useUploadFromClipboard'
import { FC, useEffect, useRef, useState } from 'react'

import CommonDialog from '../../modals/CommonDialog'

import ControlsBar from './ControlsBar'
import { TPostForm } from './types'
import { useSubmitPost } from './hooks/useSubmitPost'
import useModalHandlers from './hooks/useModalHandlers'
import CreatePostSection from './CreatePostSection'
import CreatePollSection from './CreatePollSection'
import useAddEmojiHandler from './hooks/useAddEmojiHandler'
import MobilePostForm from '../../mobile/MobilePostForm'
import UnsavedChangesModal from 'components/modals/UnsavedChangesModal'

const PostForm: FC<TPostForm> = ({
  className,
  pollClassName,
  onFocusChange,
  submitButtonText,
  authorIdentityOptions,
  authorIdentityId: initialAuthorIdentityId,
  ownerId,
  onAuthorIdentityChange,
  userMentionSearchScope,
  ...props
}) => {
  const isMobile = useIsMobile()
  const [authorIdentityId, setAuthorIdentityId] = useState<string | null>(
    initialAuthorIdentityId ?? null,
  )
  const handleAuthorIdentityChange = (value: string | null) => {
    setAuthorIdentityId(value)
    onAuthorIdentityChange?.(value)
  }

  const closeCb = useRef<(() => void) | undefined>()

  const {
    text,
    uploads,
    privacy,
    hasUploads,
    isValidText,
    isFeedLoading,
    isSubmitLoading,
    isSensitive,
    isChangedValues,
    textFieldRef,
    isPreviewLink,
    canSubmitPost,
    contentWarning,
    isAllowedComments,
    isPollStep,
    togglePollStep,
    setPrivacy,
    addMedia,
    removeMedia,
    updateMedia,
    onAllowComments,
    handleEnterKeyDown,
    handleTextareaChange,
    handleSubmitPost,
    toggleSensitive,
    setContentWarning,
    location,
    setLocation,
    clearLocation,
    ...otherProps
  } = useSubmitPost({
    ...props,
    closeCb,
    authorIdentityId,
    ownerId,
  })
  const onAddEmoji = useAddEmojiHandler({
    ref: textFieldRef,
    onSetText: otherProps.setText,
  })

  const { guardedAction } = useActionGuard()

  const container = useRef<HTMLDivElement | null>(null)

  useUploadFromClipboard({ addMedia, containerRef: container })

  const [isFocused, showFocus, , toggleFocus] = useSwitchState()

  useEffect(() => {
    onFocusChange?.(isFocused)
  }, [isFocused])

  const submitForm = guardedAction(async () => {
    await handleSubmitPost()
    toggleFocus()
  })

  const [isOpenPollEditConfirm, openPollEditConfirm, closePollEditConfirm] =
    useSwitchState()
  const [
    isOpenPollConvertConfirm,
    openPollConvertConfirm,
    closePollConvertConfirm,
  ] = useSwitchState()
  const votersCount = props.feed?.poll?.votersCount || 0
  const isVotedPoll = votersCount > 0

  const handleSubmitOrConfirm = () => {
    if (isVotedPoll) {
      openPollEditConfirm()
    } else void submitForm()
  }

  const handleTogglePollStep = () => {
    if (isPollStep) {
      openPollConvertConfirm()
    } else {
      togglePollStep()
    }
  }

  const {
    modalTitle,
    buttonText,
    handleBackModal,
    handleCloseModal,
    isOpenUnsavedChangesModal,
    closeUnsavedChangesModal,
    handleCloseOrConfirm,
  } = useModalHandlers({
    isPollStep,
    togglePollStep,
    feed: props.feed,
    closeCb,
    onModalBack: openPollConvertConfirm,
    onCloseModal: props.onCloseModal,
    modalTitleText: props.modalTitleText,
    isChangedForm: isChangedValues,
  })

  const controlsBar = (
    <ControlsBar
      {...props}
      {...{ text, contentWarning, isPollStep, canSubmitPost }}
      {...{ uploads, isValidText, privacy, setPrivacy }}
      {...{ onAllowComments, isAllowedComments }}
      submitButtonText={submitButtonText || buttonText}
      authorIdentityOptions={authorIdentityOptions}
      authorIdentityId={authorIdentityId}
      onAuthorIdentityChange={handleAuthorIdentityChange}
      isSensitive={!!isSensitive}
      isDisabledMedia={!!isPollStep}
      isLoading={isFeedLoading || isSubmitLoading}
      onSubmit={handleSubmitOrConfirm}
      onAddMedia={addMedia}
      onEmojiClick={onAddEmoji}
      onPrivacyChange={setPrivacy}
      toggleSensitive={toggleSensitive}
      setContentWarning={setContentWarning}
      togglePollStep={handleTogglePollStep}
      location={location}
      setLocation={setLocation}
      clearLocation={clearLocation}
    />
  )

  const content = (
    <div
      ref={container}
      data-testid="post-form-container"
      style={{
        height: !isFocused && props.isCompactField ? '48px' : 'auto',
      }}
      className={cn(
        `gap-xs relative z-[5] flex w-full max-w-[580px] flex-1 flex-col bg-white transition-all duration-300 ease-in-out`,
        {
          'p-xs rounded-m border': props.isReply,
          'overflow-hidden': !isFocused && props.isCompactField,
        },
        className,
        isPollStep ? pollClassName : '',
      )}
    >
      <UnsavedChangesModal
        title="Do you want to edit the poll and reset it?"
        rightButtonLabel="Save & reset"
        leftButtonLabel="Cancel"
        isOpen={isOpenPollEditConfirm}
        onDiscard={closePollEditConfirm}
        onKeepEditing={submitForm}
      />
      <UnsavedChangesModal
        isOpen={isOpenPollConvertConfirm}
        onDiscard={() => {
          togglePollStep()
          closePollConvertConfirm()
        }}
        onKeepEditing={closePollConvertConfirm}
      />
      {isPollStep ? (
        <CreatePollSection
          feed={props.feed}
          text={text}
          textInputRef={textFieldRef as React.RefObject<HTMLInputElement>}
          isLoading={isFeedLoading}
          onPollChange={otherProps.setPollData}
          onTextUpdate={(v) => otherProps.setText(v)}
        />
      ) : (
        <CreatePostSection
          card={props.feed?.card}
          placeholder={props.placeholder}
          onAddMedia={addMedia}
          onUpdateMedia={updateMedia}
          onRemoveMedia={removeMedia}
          onToggleFocus={showFocus}
          onEnterKeyDown={handleEnterKeyDown}
          onTextAreaChange={handleTextareaChange}
          textAreaRef={textFieldRef as React.RefObject<HTMLTextAreaElement>}
          userMentionSearchScope={userMentionSearchScope}
          {...{ isFocused, text, privacy, uploads, hasUploads }}
          {...{ contentWarning, isPreviewLink }}
          {...otherProps}
          autoFocus={props.autoFocus}
          isLoading={isFeedLoading}
        />
      )}
      {(isFocused || !props.isCompactField) && !props.isModal && controlsBar}
    </div>
  )

  return props.isModal ? (
    <>
      <UnsavedChangesModal
        isOpen={isOpenUnsavedChangesModal}
        onKeepEditing={closeUnsavedChangesModal}
        onDiscard={handleCloseModal}
      />
      {isMobile ? (
        <MobilePostForm
          onCloseModal={handleCloseOrConfirm}
          title={modalTitle}
          open={props.isOpenModal}
          content={content}
          footer={controlsBar}
        >
          {props.children}
        </MobilePostForm>
      ) : (
        <CommonDialog
          open={props.isOpenModal}
          skipModalAutofocus
          title={modalTitle}
          onBack={handleBackModal}
          onCloseModal={handleCloseOrConfirm}
          footerClassName="pb-0 border-0"
          childrenClassName="pb-0 max-h-[calc(90vh-125px)]"
          renderFooter={() => controlsBar}
        >
          {content}
          {props.children}
        </CommonDialog>
      )}
    </>
  ) : (
    content
  )
}

export { PostForm }
